/**
 * @file revenueApi.js
 * @description Utility functions to interact with the revenue-related backend APIs.
 *              Includes operations for retrieving, creating, updating, and deleting custom transactions.
 *              All requests are authenticated using a token stored in localStorage.
 * @author govind dindewad
 */

import axios from "axios";
import apiUrl from "./api";
import { data } from "react-router";

/**
 * @function getRevenueData
 * @description Fetches revenue transaction data with filters and pagination.
 * @param {string} searchKey - Search term to filter transactions.
 * @param {string} startDate - Filter transactions starting from this date (ISO format).
 * @param {string} endDate - Filter transactions up to this date (ISO format).
 * @param {number} pageNo - Current page number for pagination.
 * @param {string} type - Type of transaction to filter.
 * @returns {Promise<Object>} Axios response object.
 */
async function getRevenueData(searchKey, startDate, endDate, pageNo, type) {
    const token = window.localStorage.getItem('token');

    try {
        const response = await axios.get(
            `${apiUrl}secure/rev/retain-trxnc?pageNumber=${pageNo}&searchKey=${searchKey}&startDate=${startDate}&endDate=${endDate}&type=${type}`,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            }
        );
        return response;
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}

/**
 * @function postCustomTxn
 * @description Submits a new custom transaction to the backend.
 * @param {Object} txnData - The data of the transaction to create.
 * @returns {Promise<Object>} Axios response object.
 */
async function postCustomTxn(txnData) {
    const token = window.localStorage.getItem('token');

    try {
        const response = await axios.post(
            `${apiUrl}secure/rev/create-cust-trxnc`,
            txnData,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            }
        );
        return response;
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}

/**
 * @function deleteCustomTxn
 * @description Deletes a custom transaction by its ID.
 * @param {string} txnId - The ID of the transaction to delete.
 * @returns {Promise<Object>} Axios response object.
 */
async function deleteCustomTxn(txnId) {
    const token = window.localStorage.getItem('token');

    try {
        const response = await axios.delete(
            `${apiUrl}secure/rev/delete-cust-trxnc?transactionInfoId=${txnId}`,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            }
        );
        return response;
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}

/**
 * @function updateCustomTxn
 * @description Updates an existing custom transaction with new data.
 * @param {Object} txnData - The updated transaction data.
 * @returns {Promise<Object>} Axios response object.
 */
async function updateCustomTxn(txnData) {
    const token = window.localStorage.getItem('token');

    try {
        const response = await axios.put(
            `${apiUrl}secure/rev/update-cust-trxnc`,
            txnData,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            }
        );
        return response;
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}


async function getTxnReqList(pageNo=0, searchKey='') {
    const token = window.localStorage.getItem('token');

    try {
        const response = await axios.get(
            `${apiUrl}secure/rev/payment-requests?pageNumber=${pageNo}&searchKey=${searchKey}&filter=${"All"}`,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            }
        );
        return response;
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}

async function getOrderHistory(orderId) {
    const token = window.localStorage.getItem('token');

    try {
        const response = await axios.get(
            `${apiUrl}secure/transaction/details?orderId=${orderId}`,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            }
        );
        return response;
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}
async function getOrderHistorySync(orderId) {
    const token = window.localStorage.getItem('token');

    try {
        const response = await axios.get(
            `${apiUrl}secure/rev/payment-request?paymentRequestId=${orderId}`,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            }
        );
        return response;
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}

async function updateOrderHistory(data) {
    const token = window.localStorage.getItem('token');

    try {
        const response = await axios.put(
            `${apiUrl}secure/rev/update-payment-request`,data,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            }
        );
        return response;
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}
// Export all API functions
export {
    getRevenueData,
    postCustomTxn,
    deleteCustomTxn,
    updateCustomTxn,
    getTxnReqList,
    getOrderHistory,
    getOrderHistorySync,
    updateOrderHistory
};
